import { Component } from '@theme/component';

/**
 * Deferred 1080p hero video. Poster is the LCP; src is attached only when
 * autoplay is allowed (not reduced-motion) and this instance matches the viewport.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} videoElement
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
class HeroVideo extends Component {
  requiredRefs = ['videoElement'];

  /** @type {boolean} */
  #loaded = false;

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#onReducedMotionChange);

    if (this.#reducedMotion.matches) return;

    const media = this.dataset.media;
    if (media) {
      this.#mediaQuery = window.matchMedia(media);
      this.#mediaQuery.addEventListener('change', this.#onMediaChange);

      if (!this.#mediaQuery.matches) return;
    }

    this.#loadAndPlay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaQuery?.removeEventListener('change', this.#onMediaChange);
    this.#reducedMotion?.removeEventListener('change', this.#onReducedMotionChange);
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #onMediaChange = (event) => {
    if (event.matches) this.#loadAndPlay();
  };

  /**
   * @param {MediaQueryListEvent} event
   */
  #onReducedMotionChange = (event) => {
    if (event.matches) {
      this.#pauseForReducedMotion();
      return;
    }

    const media = this.dataset.media;
    if (media && !window.matchMedia(media).matches) return;

    this.#loadAndPlay();
  };

  #loadAndPlay() {
    const video = this.refs.videoElement;
    if (!(video instanceof HTMLVideoElement)) return;

    if (!this.#loaded) {
      const src = this.dataset.src;
      if (!src) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.src = src;
      video.load();
      this.#loaded = true;
      video.addEventListener('playing', this.#onPlaying);
    }

    video.play().catch(() => {});
  }

  #onPlaying = () => {
    this.setAttribute('data-playing', '');
    this.removeAttribute('data-paused');
    this.#syncPauseButton(true);
  };

  #pauseForReducedMotion() {
    const video = this.refs.videoElement;
    if (!(video instanceof HTMLVideoElement)) return;

    video.pause();
    video.removeAttribute('src');
    video.load();
    this.#loaded = false;
    this.removeAttribute('data-playing');
    this.removeAttribute('data-paused');
  }

  /**
   * @param {Event} [_event]
   */
  togglePlayback(_event) {
    const video = this.refs.videoElement;
    if (!(video instanceof HTMLVideoElement) || !this.#loaded) return;

    if (video.paused) {
      video.play().catch(() => {});
      this.setAttribute('data-playing', '');
      this.removeAttribute('data-paused');
      this.#syncPauseButton(true);
      return;
    }

    video.pause();
    this.removeAttribute('data-playing');
    this.setAttribute('data-paused', '');
    this.#syncPauseButton(false);
  }

  /**
   * @param {boolean} playing
   */
  #syncPauseButton(playing) {
    const button = this.refs.pauseButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const label = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('hero-video')) {
  customElements.define('hero-video', HeroVideo);
}
